<script lang="ts" setup>
import bottleSvg from '@/assets/images/bottle.svg'
import bundleSvg from '@/assets/images/bundle.svg'
import { API_URL } from '@/constants/GlobalConstants.js'
import {
  buildImageUrlAppendix,
  IMAGE_SIZE_LADDER
} from '@/services/helpers/ImageSizeHelper'
import UtilityHelper from '@/services/helpers/UtilityHelper'
import { useAccessibilityStore } from '@/stores/accessibility.js'
import { useHead } from '@unhead/vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'

/**
 * The PDP stage image, rendered so that the server HTML already carries the
 * final, visible <img>.
 *
 * ProductImage cannot do that: VImg hides its image until the client-side load
 * event, and the component rewrites the URL twice after mount (device pixel
 * ratio, then the measured box), so on a server-rendered PDP nothing but the
 * placeholder was visible before hydration and the image visitors finally saw
 * was the third request. Here the browser picks the density from srcset, the
 * URL never changes after the server render, and the head preloads it
 * (VIP-12365).
 */

const MIN_HERO_IMAGE_WIDTH = 440
const HERO_IMAGE_WIDTHS = IMAGE_SIZE_LADDER.filter(
  (width) => width >= MIN_HERO_IMAGE_WIDTH
)

/**
 * Mirrors ProductStage's Vuetify container and column widths. The 24 px are
 * the v-col horizontal padding; at lg and above the image occupies six of the
 * twelve columns. Keeping this identical on the img and its preload prevents
 * the browser from downloading the preload and selecting another candidate
 * for the element.
 */
const HERO_IMAGE_SIZES = [
  '(min-width: 2560px) 1176px',
  '(min-width: 1920px) 876px',
  '(min-width: 1280px) 576px',
  '(min-width: 960px) 876px',
  'calc(100vw - 24px)'
].join(', ')

const {
  productId,
  campaignId,
  height,
  alt = '',
  isBundle = false,
  schemaImage = false
} = defineProps<{
  productId?: number | string
  campaignId?: number | string
  /**
   * CSS height of the image box. The image API preserves the source ratio
   * inside each square size-ladder request.
   */
  height: number
  alt?: string
  /** Choose the existing matching placeholder while the image downloads. */
  isBundle?: boolean
  /** Mark the image as the product's schema.org image */
  schemaImage?: boolean
}>()

const { highContrastEnabled } = storeToRefs(useAccessibilityStore())

const variantUrl = (width: number) => {
  return UtilityHelper.formatString(
    API_URL.CATALOG.PRODUCT.GET_V2_IMAGE,
    productId,
    campaignId,
    buildImageUrlAppendix('image', width, width)
  )
}

const candidates = computed(() => {
  if (!productId || !campaignId) return []
  return HERO_IMAGE_WIDTHS.map((width) => ({ width, url: variantUrl(width) }))
})

const src = computed(() => candidates.value[0]?.url)
const srcset = computed(() =>
  candidates.value.map(({ url, width }) => `${url} ${width}w`).join(', ')
)
const placeholder = computed(() => (isBundle ? bundleSvg : bottleSvg))

useHead(
  computed(() => ({
    link: src.value
      ? [
          {
            key: 'product-hero-image',
            rel: 'preload',
            as: 'image',
            href: src.value,
            imagesrcset: srcset.value,
            imagesizes: HERO_IMAGE_SIZES,
            fetchpriority: 'high'
          }
        ]
      : []
  }))
)

const img = ref<HTMLImageElement>()
const loaded = ref(false)
const failed = ref(false)

onMounted(() => {
  const el = img.value
  // the error event may have fired before hydration attached the listener
  if (!el?.complete) return
  failed.value = el.naturalWidth === 0
  loaded.value = !failed.value
})

// the next product starts clean
watch(src, () => {
  loaded.value = false
  failed.value = false
})

const onLoad = () => {
  loaded.value = true
  failed.value = false
}
const onError = () => {
  loaded.value = false
  failed.value = true
}
</script>

<template>
  <div class="product-hero-image" :style="{ height: `${height}px` }">
    <img
      v-show="!loaded"
      class="product-hero-image__placeholder"
      :src="placeholder"
      alt=""
      aria-hidden="true"
      :width="height"
      :height="height"
    />
    <img
      v-if="src"
      ref="img"
      class="product-hero-image__img"
      :class="{
        'product-hero-image__img--failed': failed,
        'product-hero-image__img--high-contrast': highContrastEnabled
      }"
      :src="src"
      :srcset="srcset"
      :sizes="HERO_IMAGE_SIZES"
      :alt="alt"
      :width="height"
      :height="height"
      :itemprop="schemaImage ? 'image' : undefined"
      fetchpriority="high"
      decoding="async"
      @load="onLoad"
      @error="onError"
    />
  </div>
</template>

<style scoped>
.product-hero-image {
  position: relative;
  width: 100%;
  max-width: 100%;
}

.product-hero-image__img,
.product-hero-image__placeholder {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.product-hero-image__placeholder {
  filter: blur(4px);
}

.product-hero-image__img--failed {
  visibility: hidden;
}

.product-hero-image__img--high-contrast {
  filter: contrast(1.5);
}
</style>
